<!--
  BottomNavBar.vue
  Mobile bottom navigation bar with a "More" bottom sheet.
  Replaces the desktop nav drawer on small screens and mirrors its
  functionality exactly.

  The bar shows 5 buttons at all times:
    - Podcasts → /list-podcasts
    - Latest   → /list-latest
    - Playlist → /playlist
    - Add URL  → opens the global add-url dialog
    - More     → opens a bottom sheet with the remaining items

  The "More" bottom sheet contains:
    - Play History, Status (job queue — available to every user, not just
      admins; the endpoint is already per-user scoped), Upload an episode
      file, Settings → /settings
    - Admin (collapsible, admin-only):
        Status — All Users, Disk space, Cleanup cache, Clear cache,
        Reboot proxy, Check all podcasts, User Management, Settings
        (→ /admin/settings — server config split out of the all-users
        /settings page), Architecture (currently commented out below)
    - Logout (requires confirmation)

  All state is shared via composables — no props or emits needed.
-->
<template>
  <!-- Bottom navigation bar: 3 main routes + "More" button -->
  <v-bottom-navigation v-model="activeBottomNav" grow bg-color="background" elevation="0" border="false">
    <v-btn value="list-podcasts" @click="router.push({ name: 'list-podcasts' })">
      <v-icon icon="mdi-podcast" size="x-large"></v-icon>
    </v-btn>
    <v-btn value="list-latest" @click="router.push({ name: 'list-latest' })">
      <v-icon icon="mdi-history" size="x-large"></v-icon>
    </v-btn>
    <v-btn value="playlist" @click="router.push({ name: 'playlist' })">
      <v-icon icon="mdi-playlist-music-outline" size="x-large"></v-icon>
    </v-btn>
    <!-- Add URL button: if the clipboard holds a URL, queue it directly;
         otherwise open the add-url dialog (see handleAddUrl). -->
    <v-btn @click="handleAddUrl">
      <v-icon icon="mdi-link" size="x-large"></v-icon>
    </v-btn>
    <!-- "More" button opens the bottom sheet with all remaining nav items -->
    <v-btn @click="moreSheet = true">
      <v-icon icon="mdi-dots-horizontal" size="x-large"></v-icon>
    </v-btn>
  </v-bottom-navigation>

  <!-- "More" Bottom Sheet: secondary nav items that don't fit in the bar.
       Mirrors the episode/podcast context menus exactly (v-sheet, hide-overlay,
       no-click-animation, px-2 list) so it shares the same floating width/layout. -->
  <v-bottom-sheet v-model="moreSheet" inset hide-overlay no-click-animation>
    <v-sheet>
      <v-list class="px-2" density="comfortable">
        <!-- Search episodes: navigates to the global episode search page -->
        <v-list-item
          prepend-icon="mdi-magnify"
          title="Search episodes"
          @click="moreSheet = false; router.push({ name: 'search' })"
        ></v-list-item>

        <v-divider class="my-1"></v-divider>

        <!-- Play History: episodes the user has played -->
        <v-list-item
          prepend-icon="mdi-play-box-multiple-outline"
          title="Play History"
          @click="moreSheet = false; router.push({ name: 'history' })"
        ></v-list-item>

        <!-- Status: job queue status (downloads, metadata, monitor checks,
             AI summaries). Already per-user gated server-side — used to be
             admin-only here (see NavDrawer.vue for the desktop equivalent
             of this move), which just hid a regular user's own status page
             from them for no reason. Same icon as the admin-submenu entry
             it replaces. -->
        <v-list-item
          prepend-icon="mdi-clock"
          title="Status"
          @click="moreSheet = false; router.push({ name: 'status' })"
        ></v-list-item>

        <!-- Upload a file or a text: sideload a local audio/video file, or
             make an episode from text read aloud (the page's "Text to speech" mode) -->
        <v-list-item
          prepend-icon="mdi-file-upload-outline"
          title="Upload a file or a text"
          @click="moreSheet = false; router.push({ name: 'sideload' })"
        ></v-list-item>

        <!-- Articoli Lamendola: the fides-et-ratio.it archive viewer.
             Admin-only, matching its routes and its router meta — showing it
             to everyone else would just lead to a 403. Orange rather than the
             Admin submenu's primary, so it reads as its own thing and not as
             a stray admin action. -->
        <v-list-item
          v-if="isAdmin"
          @click="moreSheet = false; router.push({ name: 'fides' })"
        >
          <template v-slot:prepend>
            <v-icon icon="mdi-book-open-page-variant" color="orange"></v-icon>
          </template>
          <v-list-item-title class="text-orange">Articoli Lamendola</v-list-item-title>
        </v-list-item>

        <!-- Settings: navigates to the settings page -->
        <v-list-item
          prepend-icon="mdi-account-cog"
          title="Settings"
          @click="moreSheet = false; router.push({ name: 'settings' })"
        ></v-list-item>

        <!-- Admin submenu: collapsible group, no indentation on children -->
        <v-list-group v-if="isAdmin" value="admin">
          <template v-slot:activator="{ props }">
            <v-list-item v-bind="props">
              <template v-slot:prepend><v-icon icon="mdi-shield-crown" color="primary"></v-icon></template>
              <v-list-item-title class="text-primary">Admin</v-list-item-title>
            </v-list-item>
          </template>

          <!-- Status — All Users: the deliberately SEPARATE admin-only view
               of every user's job queue (GET /queue/status/all-users),
               distinct from the personal "Status" entry above the divider —
               different icon so the two are easy to tell apart. -->
          <v-list-item @click="moreSheet = false; router.push({ name: 'admin-status' })">
            <template v-slot:prepend><v-icon icon="mdi-account-multiple-outline" color="primary"></v-icon></template>
            <v-list-item-title class="text-primary">Status — All Users</v-list-item-title>
          </v-list-item>

          <!-- Storage: consolidated dialog (server disk, proxy disk, R2 cache, R2 saved) -->
          <v-list-item @click="moreSheet = false; storageDialog = true">
            <template v-slot:prepend><v-icon icon="mdi-harddisk" color="primary"></v-icon></template>
            <v-list-item-title class="text-primary">Storage</v-list-item-title>
          </v-list-item>

          <!-- Cache cleanup (expired files only) -->
          <v-list-item @click="moreSheet = false; handleCleanup()">
            <template v-slot:prepend><v-icon icon="mdi-broom" color="primary"></v-icon></template>
            <v-list-item-title class="text-primary">Cleanup cache</v-list-item-title>
          </v-list-item>

          <!-- Clear all cache (destructive — opens centralized confirm dialog) -->
          <v-list-item @click="moreSheet = false; handleClearCache()">
            <template v-slot:prepend><v-icon icon="mdi-close-circle-outline" color="primary"></v-icon></template>
            <v-list-item-title class="text-primary">Clear cache</v-list-item-title>
          </v-list-item>

          <!-- Reboot proxy server (opens centralized confirm dialog) -->
          <v-list-item @click="moreSheet = false; handleRebootProxy()">
            <template v-slot:prepend><v-icon icon="mdi-lightning-bolt-circle" color="primary"></v-icon></template>
            <v-list-item-title class="text-primary">Reboot proxy</v-list-item-title>
          </v-list-item>

          <!-- Check all podcasts for new episodes -->
          <v-list-item @click="moreSheet = false; handleCheckPodcasts()">
            <template v-slot:prepend><v-icon icon="mdi-refresh" color="primary"></v-icon></template>
            <v-list-item-title class="text-primary">Check all podcasts</v-list-item-title>
          </v-list-item>

          <!-- User management page -->
          <v-list-item @click="moreSheet = false; router.push({ name: 'admin-users' })">
            <template v-slot:prepend><v-icon icon="mdi-account-group" color="primary"></v-icon></template>
            <v-list-item-title class="text-primary">User Management</v-list-item-title>
          </v-list-item>

          <!-- Server Settings: admin-only half of what used to be one mixed
               /settings page — different icon from the all-users "Settings"
               item above so the two are easy to tell apart. -->
          <v-list-item @click="moreSheet = false; router.push({ name: 'admin-settings' })">
            <template v-slot:prepend><v-icon icon="mdi-tune" color="primary"></v-icon></template>
            <v-list-item-title class="text-primary">Settings</v-list-item-title>
          </v-list-item>

          <!-- Architecture documentation nav entry — commented out (not
               deleted), route/component ("admin-architecture") still exist.
          <v-list-item @click="moreSheet = false; router.push({ name: 'admin-architecture' })">
            <template v-slot:prepend><v-icon icon="mdi-file-document-outline" color="primary"></v-icon></template>
            <v-list-item-title class="text-primary">Architecture</v-list-item-title>
          </v-list-item>
          -->
        </v-list-group>

        <v-divider class="my-2"></v-divider>

        <!-- Logout: opens the centralized confirm dialog -->
        <v-list-item
          prepend-icon="mdi-logout"
          title="Logout"
          @click="moreSheet = false; handleLogout()"
        ></v-list-item>
      </v-list>
    </v-sheet>
  </v-bottom-sheet>

  <!-- Consolidated storage report dialog (server/proxy disk + R2 cache/saved) -->
  <storage-dialog v-model="storageDialog"></storage-dialog>
</template>

<script setup>
/**
 * BottomNavBar.vue - Script Section
 *
 * Mobile-only bottom navigation component. Provides the same full set of
 * navigation and admin actions as the desktop NavDrawer. Uses composables
 * for all shared state (snackbar, dialogs, auth) and imports API functions
 * directly for admin actions (disk space, saved stats, cache, proxy, podcasts).
 *
 * Admin actions that are destructive (clear cache, reboot proxy, logout)
 * go through the centralized ConfirmDialog before executing.
 */

import { ref, computed } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useLogin } from "@/composables/useLogin.js";
import { useAppMenu } from "@/composables/useAppMenu.js";
import { useConfirmDialog } from "@/composables/useConfirmDialog.js";
import {
  cleanup,
  clear,
  checkPodcasts,
  rebootProxy,
} from "@/api/index.js";
import { logout } from "@/services/logout.js";
import { episodesApi } from "@/api/episodes.js";
import StorageDialog from "./StorageDialog.vue";

const router = useRouter();
const route = useRoute();
const { isAdmin } = useLogin();
const { confirm } = useConfirmDialog();

// Shared state from useAppMenu composable
const {
  snackbar,
  addUrlDialog,
} = useAppMenu();

// Controls the "More" bottom sheet visibility (local to this component)
const moreSheet = ref(false);

// Controls the consolidated Storage report dialog
const storageDialog = ref(false);

/** isHttpUrl - true only for well-formed http(s) URLs. */
const isHttpUrl = (s) => {
  if (!s) return false;
  try {
    const u = new URL(s);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
};

/**
 * handleAddUrl - Add URL button handler.
 * Reads the clipboard (the button click is the required user gesture). If it
 * holds a valid http(s) URL, queue it straight to the server and confirm via
 * snackbar. Otherwise — clipboard empty, not a URL, or clipboard access is
 * unavailable/denied (e.g. non-secure context, Firefox, or a mobile OS that
 * blocks/declines the read) — fall back to opening the Add URL dialog.
 */
const handleAddUrl = async () => {
  let clip = "";
  try {
    clip = (await navigator.clipboard?.readText())?.trim() || "";
  } catch {
    clip = ""; // permission denied / unsupported → fall back to the dialog
  }

  if (!isHttpUrl(clip)) {
    addUrlDialog.value = true;
    return;
  }

  try {
    await episodesApi.addUrl(clip);
    const shown = clip.length > 48 ? clip.slice(0, 48) + "…" : clip;
    snackbar.value = { show: true, color: "success", message: `Queued: ${shown}` };
  } catch (error) {
    snackbar.value = {
      show: true,
      color: "error",
      message: `Failed to add URL: ${error?.message || error}`,
    };
  }
};

/**
 * activeBottomNav - Tracks which bottom nav button is active based on
 * the current route. Maps child routes (e.g. list-episodes) to their
 * parent nav item (list-podcasts).
 */
const activeBottomNav = computed(() => {
  const name = route.name;
  if (name === 'list-podcasts' || name === 'list-episodes') return 'list-podcasts';
  if (name === 'list-latest') return 'list-latest';
  if (name === 'playlist') return 'playlist';
  return undefined; // No button highlighted for other routes
});

// ── Admin action handlers ───────────────────────────────────────────

// Disk-space and saved-stats are now consolidated into the Storage dialog
// (StorageDialog.vue, opened via the "Storage" menu item).

/** handleCleanup - Triggers server-side cache cleanup (deletes expired files). */
const handleCleanup = async () => {
  try {
    const response = await cleanup();
    snackbar.value = { show: true, color: "info", message: response.message };
  } catch (error) {
    snackbar.value = { show: true, color: "error", message: "Error cleaning up cache" };
  }
};

/** handleCheckPodcasts - Triggers a bulk check of all podcasts for new episodes.
 *  Shows an immediate snackbar since the server check can take several minutes. */
const handleCheckPodcasts = async () => {
  // Show immediate feedback so the user knows the action was triggered
  snackbar.value = { show: true, color: "info", message: "Checking all podcasts..." };
  try {
    const response = await checkPodcasts();
    snackbar.value = { show: true, color: "success", message: response.message };
  } catch (error) {
    snackbar.value = { show: true, color: "error", message: "Error checking podcasts" };
  }
};

/** handleClearCache - Confirms before clearing all cached files. */
const handleClearCache = async () => {
  const ok = await confirm({
    title: "Confirm clearing the cache?",
    text: "This will delete all files in the cache",
  });
  if (!ok) return;
  try {
    const response = await clear();
    snackbar.value = { show: true, color: "info", message: response.message };
  } catch (error) {
    snackbar.value = { show: true, color: "error", message: "Error clearing cache" };
  }
};

/** handleRebootProxy - Confirms before rebooting the remote proxy server. */
const handleRebootProxy = async () => {
  const ok = await confirm({
    title: "Confirm proxy reboot?",
    text: "This will reboot the remote proxy server used to download the episode files (audio, video)",
  });
  if (!ok) return;
  try {
    const response = await rebootProxy();
    snackbar.value = { show: true, color: "info", message: response.message };
  } catch (error) {
    snackbar.value = { show: true, color: "error", message: "Error rebooting proxy server" };
  }
};

/** handleLogout - Confirms before logging out. */
const handleLogout = async () => {
  const ok = await confirm({
    title: "Confirm Logout",
    text: "Are you sure you want to logout FYT?",
  });
  if (ok) {
    logout();
  }
};
</script>

<style scoped>
/* Remove default indentation from admin list group children */
:deep(.v-list-group__items .v-list-item) {
  padding-inline-start: 16px !important;
}

/* NOTE: there were three `:deep(.v-bottom-navigation ...)` rules here (dimming
 * inactive icons, and clearing the active button's background/overlay). They
 * never applied and were removed 2026-09-16.
 *
 * A LEADING `:deep()` compiles to `[data-v-x] <inner>` — it needs a scoped
 * element that is an ANCESTOR of the match. It therefore can never reach this
 * component's own ROOT element, and .v-bottom-navigation IS that root, so those
 * selectors had nothing to match anywhere in the document. (The
 * `.v-list-group__items` rule above is fine: the bottom sheet's <v-list> is
 * written in this template, carries the scope id, and really is an ancestor.)
 *
 * To style the bar itself, use a plain selector — in a scoped block that
 * compiles to `.v-bottom-navigation[data-v-x]`, which does match the root — or
 * put it in a global <style> block.
 */
</style>
